/** «Премия» — ровно то, что отдают `GET /api/bonus/report` и `GET /api/bonus/department`. */
import type { Окно } from "@/components/DateRangePicker";
import { apiFetch, query } from "@/lib/api";
import { iso, plusDays } from "@/lib/dates";

export type BonusRow = {
  id: number;
  at: string;
  amount: number;
  deal_id: number | null;
  lead_id: number | null;
  comment: string | null;
  /** Откат: проводка «Корректировки», стоящая в том начислении, которое она откатывает. */
  correction: boolean;
};

type Sum = { total: number; count: number };
type Split = Sum & { accrued: number; written_off: number };

export type BonusModule<T = Sum> = T & { id: number; name: string; rule: string | null; manual: boolean };
export type BonusCategory<T = Sum> = Sum & { id: number; name: string; modules: BonusModule<T>[] };

export type Place = { id: number; name: string };

export type UserBonus = Split & {
  from: string;
  to: string;
  user: Place;
  rows_limit: number;
  categories: BonusCategory<Split & { rows: BonusRow[] }>[];
  days: (Sum & { date: string })[];
  /** Можно ли смотреть чужие премии; без права `staff` и `locations` пусты. */
  can_all: boolean;
  staff: Place[];
  locations: Place[];
};

export type BonusPerson = Split & {
  user_id: number;
  name: string;
  active: boolean;
  /** Ключ — id начисления. */
  cells: Record<string, Sum>;
};

export type DepartmentBonus = Split & {
  from: string;
  to: string;
  location: Place;
  locations: Place[];
  categories: BonusCategory[];
  people: BonusPerson[];
};

type Period = { from: string; to: string };

export function loadUserBonus(p: Period & { user: number | null; location: number }): Promise<UserBonus> {
  return apiFetch<UserBonus>(`/api/bonus/report${query(p)}`);
}

export function loadDepartmentBonus(p: Period & { location: number }): Promise<DepartmentBonus> {
  return apiFetch<DepartmentBonus>(`/api/bonus/department${query(p)}`);
}

export const KC = 3;

/** Окна «Премии»: день и расчётные периоды — неделя и месяц. Считаются на каждый рендер. */
export function окнаПремии(): Окно[] {
  const today = new Date();
  const t = iso(today);
  const y = iso(plusDays(today, -1));
  const monday = plusDays(today, -((today.getDay() + 6) % 7));
  const month = new Date(today.getFullYear(), today.getMonth(), 1);
  return [
    { label: "сегодня", hint: "только сегодня", from: t, to: t },
    { label: "вчера", hint: "только вчера", from: y, to: y },
    { label: "эта неделя", hint: "с понедельника по сегодня", from: iso(monday), to: t },
    { label: "прошлая неделя", hint: "с понедельника по воскресенье", from: iso(plusDays(monday, -7)), to: iso(plusDays(monday, -1)) },
    { label: "этот месяц", hint: "с первого числа по сегодня", from: iso(month), to: t },
    {
      label: "прошлый месяц",
      hint: "с первого по последнее число",
      from: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: iso(plusDays(month, -1)),
    },
  ];
}

/** Деньги: «1 325», копейки — только если есть. */
export function money(v: number): string {
  return v.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

export type ZapisMethod = "link" | "chat" | "call" | "other";
export type ZapisWhere = "bitrix" | "chats" | "ext" | "link" | "auto" | "robot";

export type ZapisRow = {
  deal_id: number;
  bron_at: string;
  zapis_at: string;
  user_id: number | null;
  user_name: string | null;
  where: ZapisWhere;
  method: ZapisMethod;
  /** Канал чата: whatsapp, telegram, max, avito, vk, instagram. */
  channel: string | null;
  /** Разговор сотрудника с клиентом незадолго до записи, если был. */
  call_at: string | null;
  /** paid — начислено; due — начислит prokat; legacy — начислит крон CRM; none — не за что. */
  status: "paid" | "due" | "none";
  billing: { id: number; user_id: number; user_name: string; amount: number; own: boolean } | null;
};

export type ZapisPerson = {
  user_id: number;
  name: string;
  count: number;
  methods: Record<ZapisMethod, number>;
  paid: number;
  due: number;
};

export type ZapisBonus = {
  from: string;
  to: string;
  /** Премия за одну запись, ₽. */
  amount: number;
  count: number;
  /** Броней без записи (клиент пришёл сам); null — отчёт по одному сотруднику. */
  walk_in: number | null;
  /** Броней, где клиент записался сам (рассылка, сайт): сотрудника нет, в списке их нет. */
  self: number | null;
  paid: number;
  due: number;
  due_count: number;
  methods: Record<ZapisMethod, number>;
  people: ZapisPerson[];
  staff: Place[];
  rows: ZapisRow[];
  can_all: boolean;
  /** Фон доначисления включён: записи «к начислению» prokat оплатит сам. */
  auto: boolean;
};

export function loadZapisBonus(p: Period & { user: number | null }): Promise<ZapisBonus> {
  return apiFetch<ZapisBonus>(`/api/bonus/zapis${query(p)}`);
}
